import { Dialog, DialogContent, DialogProps, DialogTitle, Divider, useTheme } from '@material-ui/core';
import { Form, Formik, FormikHelpers } from 'formik';
import React from 'react';
import * as yup from 'yup';
import { CancelSubmitFormDialogActions } from 'framework/forms/CancelSubmitFormDialogActions';
import { FormExtendedRadioGroupField } from 'framework/forms/FormExtendedRadioGroupField';
import { handleValidateResponse } from 'framework/forms/utils/handleValidateResponse';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { ILinkAudiologistWithRfiaModel, IRequestForInsuranceAllowance, rfiasCommand_setAudiologist } from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import { FormSelectAudiologistField } from '../../../settings/users/forms/FormSelectAudiologistField';

const createSchema = (strings: IStrings) => {
	return yup.object<ILinkAudiologistWithRfiaModel>({
		audiologistId: yup.string().required(strings.formRequired(strings.audiologist)),
		alsoUpdateAttendingAudiologistOnPatient: yup.boolean().defined(),
	});
};

interface IProps extends DialogProps {
	item: IRequestForInsuranceAllowance;
	confirm: VoidFunction;
	cancel: VoidFunction;
}

export const LinkAudiologistWithRfiaForm = ({ item, confirm, cancel, ...rest }: IProps) => {
	const strings = useLocalization();
	const theme = useTheme();
	const [link, isSubmitting] = useFormSubmit(rfiasCommand_setAudiologist);

	const handleSubmit = async (values: ILinkAudiologistWithRfiaModel, helpers: FormikHelpers<ILinkAudiologistWithRfiaModel>) => {
		const r = await link(item.id, values);
		if (handleValidateResponse(r, helpers, 'audiologistId')) {
			confirm();
		}
	};

	return (
		<Formik<ILinkAudiologistWithRfiaModel>
			validateOnMount
			initialValues={{
				audiologistId: item.audiologist?.id,
				alsoUpdateAttendingAudiologistOnPatient: true,
			}}
			validationSchema={createSchema(strings)}
			onSubmit={handleSubmit}>
			<Form>
				<Dialog
					fullWidth
					{...rest}>
					<DialogTitle>{strings.selectWhat(strings.audiologist)}</DialogTitle>
					<DialogContent
						className='df-col'
						dividers>
						<FormSelectAudiologistField<ILinkAudiologistWithRfiaModel>
							name='audiologistId'
							label={strings.audiologist}
						/>
						<Divider style={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(1) }} />
						<FormExtendedRadioGroupField<ILinkAudiologistWithRfiaModel, boolean>
							name='alsoUpdateAttendingAudiologistOnPatient'
							label=''
							options={[
								{ value: true, label: strings.alsoChangeWithPatient, caption: strings.attendingAudiologistWillAlsoBeChangedWithPatient },
								{ value: false, label: strings.doNotChangeWithPatient, caption: strings.attendingAudiologistWillNotBeChangedWithPatient },
							]}
						/>
					</DialogContent>
					<CancelSubmitFormDialogActions
						cancel={cancel}
						isSubmitting={isSubmitting}
						submitText={strings.link}
					/>
				</Dialog>
			</Form>
		</Formik>
	);
};
